import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getVisiblePaginationPages } from "../../../components/PaginationBar/getVisiblePaginationPages";

interface DashboardPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function DashboardPagination({
  currentPage,
  totalPages,
  onPageChange,
}: DashboardPaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const safeCurrent = Math.min(Math.max(1, currentPage), totalPages);
  const visiblePages = getVisiblePaginationPages(safeCurrent, totalPages);

  return (
    <nav
      className="mt-2 flex flex-wrap items-center justify-center gap-1"
      aria-label="Pagination"
    >
      <button
        type="button"
        aria-label="Página anterior"
        disabled={safeCurrent <= 1}
        onClick={() => onPageChange(safeCurrent - 1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-transparent text-[#8b949e] transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {visiblePages.map((token, index) =>
        token === "ellipsis" ? (
          <span
            key={`ellipsis-${index}`}
            className="min-w-[1.25rem] px-1 text-center text-xs font-semibold text-[#8b949e]"
            aria-hidden
          >
            …
          </span>
        ) : (
          <button
            key={token}
            type="button"
            aria-current={token === safeCurrent ? "page" : undefined}
            onClick={() => onPageChange(token)}
            className={`min-w-[2rem] rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              token === safeCurrent
                ? "border border-[#6ea8ff]/35 bg-[#6ea8ff]/20 text-[#6ea8ff]"
                : "bg-transparent text-[#8b949e] hover:bg-white/5"
            }`}
          >
            {token}
          </button>
        )
      )}

      <button
        type="button"
        aria-label="Próxima página"
        disabled={safeCurrent >= totalPages}
        onClick={() => onPageChange(safeCurrent + 1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-transparent text-[#8b949e] transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}
